import { Dispatch, SetStateAction, useState } from "react";
import classNames from "classnames";
import { CloseIcon, MenuIcon, ArrowRightIcon } from "@talladega/icons";
import LocaleButton from "@common/components/LocaleButton";
import Link from "next/link";
import { useTranslation } from "next-i18next";
import { User } from "@modules/user/types/user";
import { LoginModalMode, loginMode } from "@modules/user/types/LoginModalMode";
import { TranslationKeys } from "src/@types/i18next";
import { createPortal } from "react-dom";
import useLogoutUserMutation from "@modules/user/hooks/useLogoutUserMutation";
import useLogResumeClickMutation from "@common/hooks/useLogResumeClickMutation";
import CustomerAvatar from "@components/CustomerAvatar";
import useClientSide from "@common/hooks/useClientSide";
import { tryGetActiveAftermarketPurchase } from "@modules/purchase/warranty/types/Purchase";
import useAftermarketPurchase from "@modules/purchase/warranty/hooks/useAftermarketPurchase";
import {
  aftermarketDetailsStep,
  aftermarketQuoteStep,
  getStepById,
  showResumeForStep,
  showResumeQuoteStep,
} from "@modules/purchase/PurchaseStep";
import { useRouter } from "next/router";
import { Button } from "@talladega/components";
import LocalImage from "@common/components/LocalImage";
import { roadwiseDarkLogo, roadwiseWhiteLogo } from "@public/images";
import DealCustomerTypeEnum from "@common/enums/DealCustomerTypeEnum";
import { getProvinceById } from "@talladega/types";
import { sources } from "@common/enums/SourceEnum";
import AvatarMenu from "./AvatarMenu";

const navLinks: { href: string; key: TranslationKeys["common"] }[] = [
  { href: "/", key: "common:home" },
  { href: "/about", key: "common:why_roadwise" },
  { href: "/contact", key: "common:contact_us" },
  { href: "/faq", key: "common:faq" },
];

type MobileNavBarProps = {
  user: User | undefined;
  isMenuOpen: boolean;
  onMenuOpenChange?: (isOpen: boolean) => void;
  onLoginClick: () => void;
  onLogoutClick: () => void;
  mode?: "dark" | "light";
};

const MobileNavBar = ({
  user,
  isMenuOpen,
  onMenuOpenChange,
  onLoginClick,
  onLogoutClick,
  mode,
}: MobileNavBarProps) => {
  const isClient = useClientSide();
  const { t } = useTranslation(["common", "purchase"]);

  return isClient
    ? createPortal(
        <div
          className={classNames(
            "fixed inset-x-0 top-[88px] z-50 mt-2 h-[calc(100vh-96px)] lg:hidden overflow-y-auto",
            {
              hidden: !isMenuOpen,
              "bg-secondary-900": mode === "dark",
              "bg-white": mode === "light",
            }
          )}
        >
          <div className="mx-4 h-full border-t border-white/10">
            <div className="flex h-full flex-col justify-between gap-8">
              <div className="mt-8 flex flex-col space-y-6">
                {user?.isLoggedIn && (
                  <Link
                    key="profile"
                    href="/profile"
                    className={classNames("hover:text-opacity-80", {
                      "text-white": mode === "dark",
                      "text-gray-900": mode === "light",
                    })}
                  >
                    {t("common:my_profile")}
                  </Link>
                )}
                {user?.isLoggedIn && (
                  <Link
                    key="protection"
                    href="/protection"
                    className={classNames("hover:text-opacity-80", {
                      "text-white": mode === "dark",
                      "text-gray-900": mode === "light",
                    })}
                  >
                    {t("common:warranty_protection")}
                  </Link>
                )}

                {navLinks.map(({ href, key }) => (
                  <Link
                    key={href}
                    href={href}
                    className={classNames("hover:text-opacity-80", {
                      "text-white": mode === "dark",
                      "text-gray-900": mode === "light",
                    })}
                    onClick={() => onMenuOpenChange?.(false)}
                  >
                    {t(key)}
                  </Link>
                ))}
              </div>
              <div className="flex flex-col items-center gap-2 pb-4">
                <Link
                  href="/"
                  className="w-full rounded-full border-none bg-primary-600 px-4 py-2 text-center text-white hover:bg-primary-600"
                >
                  {t("purchase:get_a_free_quote")}
                </Link>
                {user && user.isLoggedIn ? (
                  <Link
                    href="#"
                    onClick={onLogoutClick}
                    className={classNames("w-full text-center", {
                      "text-white": mode === "dark",
                      "text-primary-700": mode === "light",
                    })}
                  >
                    {t("common:logout")}
                  </Link>
                ) : (
                  <Link
                    href="#"
                    onClick={onLoginClick}
                    className={classNames("w-full text-center", {
                      "text-white": mode === "dark",
                      "text-primary-700": mode === "light",
                    })}
                  >
                    {t("common:login")}
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;
};

type Props = {
  setShowLoginModal: Dispatch<SetStateAction<boolean>>;
  setLoginModalMode: Dispatch<SetStateAction<LoginModalMode>>;
  user: User | undefined;
  showResumePurchase?: boolean;
  mode?: "dark" | "light";
};

const NavBar = ({
  setShowLoginModal,
  setLoginModalMode,
  user,
  showResumePurchase,
  mode = "dark",
}: Props) => {
  const { t } = useTranslation(["common", "purchase"]);
  const logoutMutation = useLogoutUserMutation();
  const { mutate: logResumeClickMutation } = useLogResumeClickMutation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);

  const onLoginClick = () => {
    setLoginModalMode(loginMode);
    setShowLoginModal(true);
  };
  const onAvatarClick = () => {
    setIsAvatarMenuOpen(!isAvatarMenuOpen);
  };
  const onLogoutClick = () => {
    setIsAvatarMenuOpen(false);
    if (user?.isLoggedIn) {
      logoutMutation.mutate();
    }
  };

  const router = useRouter();
  const locale = router.locale || "en";

  const { purchase: purchaseResponse } = useAftermarketPurchase();
  const purchase = tryGetActiveAftermarketPurchase(purchaseResponse);

  const mainApplicant = purchase?.customers.find(
    (c) => c.typeId === DealCustomerTypeEnum.MainApplicant
  );

  const provinceAbbr = mainApplicant?.currentAddress?.provinceId
    ? getProvinceById(mainApplicant.currentAddress.provinceId)?.abbr
    : "";

  const odometer = purchase?.vehicle?.odometerCount;
  const vin = purchase?.vehicle?.vin;
  const year = purchase?.vehicle?.year;
  const make = purchase?.vehicle?.make;
  const model = purchase?.vehicle?.model;

  const hasValidVehicleInfo = !!vin || (!!year && !!make && !!model);

  const hasProvince = !!provinceAbbr;
  const hasOdometer = !!odometer;

  const showResumeButton =
    !!purchase && showResumeForStep(purchase.currentStepId);

  const showResumeQuoteButton =
    !!purchase &&
    showResumeQuoteStep(purchase.currentStepId) &&
    hasValidVehicleInfo &&
    hasProvince &&
    hasOdometer;

  const handleOnResumePurchaseClick = () => {
    if (!purchase) return;

    const purchaseStep = getStepById(
      "aftermarket",
      purchase?.currentStepId ?? aftermarketDetailsStep.id
    );

    if (purchaseStep) {
      logResumeClickMutation(purchase.id);
      router.push({
        pathname: purchaseStep.href,
      });
    }
  };

  const handleOnResumeQuoteClick = () => {
    if (!purchase) return;

    if (hasValidVehicleInfo && hasProvince && hasOdometer) {
      const quoteParams: Record<string, string> = {
        source:
          sources
            .find((s) => s.name === purchase.dealSource.toString())
            ?.name.toLowerCase() || "",
        odometer: odometer.toString(),
        province: provinceAbbr,
        removeDiscount: "true",
      };

      if (vin) {
        quoteParams.vin = vin;
      } else {
        quoteParams.year = year?.toString() || "";
        quoteParams.make = make || "";
        quoteParams.model = model || "";
        quoteParams.trim = purchase.vehicle?.trim || "";
      }

      const { href } = aftermarketQuoteStep;
      router.push(`${href}?${new URLSearchParams(quoteParams).toString()}`);
    }
  };

  const pathname = router?.pathname;

  const showResume = showResumePurchase && showResumeButton;
  const showResumeQuote =
    showResumePurchase && showResumeQuoteButton && !pathname.includes("/quote");

  return (
    <nav
      className={classNames("sticky top-0 z-50 py-2", {
        "bg-secondary-900": mode === "dark",
        "bg-white border border-b-2": mode === "light",
      })}
    >
      <div className="mx-6 flex items-center justify-between lg:mx-14 xl:mx-32">
        <div className="flex">
          <Link href="/">
            {mode === "dark" && (
              <LocalImage
                className="w-[144px] h-[80px]"
                width="144"
                height="80"
                src={
                  locale === "en"
                    ? roadwiseWhiteLogo.srcEn
                    : roadwiseWhiteLogo.srcFr
                }
                alt="Roadwise Logo"
                priority
              />
            )}
            {mode === "light" && (
              <LocalImage
                className="w-[144px] h-[80px]"
                width="144"
                height="80"
                src={
                  locale === "en"
                    ? roadwiseDarkLogo.srcEn
                    : roadwiseDarkLogo.srcFr
                }
                alt="Roadwise Logo"
                priority
              />
            )}
          </Link>
        </div>

        {/* Desktop Nav */}
        <div className="hidden items-center gap-8 font-normal lg:flex">
          <div className="hidden ml-9 items-center gap-8 font-normal leading-4 lg:flex">
            {navLinks.map(({ href, key }) => (
              <Link
                key={href}
                href={href}
                className={classNames("hover:text-opacity-80", {
                  "text-white": mode === "dark",
                  "text-gray-900": mode === "light",
                })}
              >
                {t(key)}
              </Link>
            ))}{" "}
          </div>
          <LocaleButton mode={mode} />
          <hr className="hidden h-12 border-l-[1px] border-gray-400 lg:block" />

          <div className="flex items-center gap-4">
            {!showResume && !showResumeQuote && (
              <Link
                href="/"
                className={classNames("rounded-xl border px-4 py-2", {
                  "bg-white hover:bg-white/80": mode === "dark",
                  "bg-primary-700 text-white": mode === "light",
                })}
              >
                {t("purchase:get_a_free_quote")}
              </Link>
            )}
            {showResume && (
              <Button
                rightIcon={<ArrowRightIcon />}
                onClick={handleOnResumePurchaseClick}
              >
                {t("purchase:resume_purchase")}
              </Button>
            )}
            {showResumeQuote && (
              <Button
                rightIcon={<ArrowRightIcon />}
                onClick={handleOnResumeQuoteClick}
              >
                {t("purchase:view_my_quote")}
              </Button>
            )}
            {user && user.isLoggedIn ? (
              <Link
                href="#"
                onClick={onAvatarClick}
                onMouseEnter={onAvatarClick}
                className={classNames("hover:text-opacity-80", {
                  "text-white": mode === "dark",
                  "text-gray-900": mode === "light",
                })}
              >
                <CustomerAvatar customer={user} size="sm" isCurrentUser />
              </Link>
            ) : (
              <Link
                href="#"
                onClick={onLoginClick}
                className={classNames("hover:text-opacity-80", {
                  "text-white": mode === "dark",
                  "text-primary-700": mode === "light",
                })}
              >
                {t("common:login")}
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Nav */}
        <div className="flex items-center space-x-4 lg:hidden">
          <LocaleButton mode={mode} />
          <button
            type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle Menu"
          >
            {isMenuOpen ? (
              <CloseIcon
                className={classNames("h-6 w-6", {
                  "text-white": mode === "dark",
                  "text-gray-900": mode === "light",
                })}
              />
            ) : (
              <MenuIcon
                className={classNames("h-6 w-6", {
                  "text-white": mode === "dark",
                  "text-gray-900": mode === "light",
                })}
              />
            )}
          </button>
        </div>
      </div>

      <MobileNavBar
        user={user}
        isMenuOpen={isMenuOpen}
        onMenuOpenChange={setIsMenuOpen}
        onLoginClick={onLoginClick}
        onLogoutClick={onLogoutClick}
        mode={mode}
      />

      {user && isAvatarMenuOpen && (
        <AvatarMenu
          onLogoutClick={onLogoutClick}
          user={user}
          activePurchase={purchase}
        />
      )}
    </nav>
  );
};

export default NavBar;
